import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Loader2, CheckCircle2, AlertCircle, Calendar as CalendarIcon, Calculator, Package } from "lucide-react";
import { BackButton } from "../components/BackButton";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { computeSuggestedPrice, ZONE_OPTIONS, VEHICLE_TYPES, suggestVehicleByWeightVolume, isVehicleCompatible, classifyMMLClass } from "../lib/pricing";

const ZONE_LABEL = {
  city: "Urbain dense",
  countryside: "Campagne / route",
  mixed: "Mixte",
};

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

async function geocode({ street, postal_code, city }) {
  if (process.env.REACT_APP_DEMO_MODE === "1") {
    const known = {
      "31": { lat: 43.6045, lng: 1.444, name: "Toulouse" },
      "34": { lat: 43.6108, lng: 3.8767, name: "Montpellier" },
      "69": { lat: 45.7608, lng: 4.8422, name: "Lyon" },
      "75": { lat: 48.8566, lng: 2.3522, name: "Paris" },
      "33": { lat: 44.8378, lng: -0.5792, name: "Bordeaux" },
      "13": { lat: 43.2965, lng: 5.3698, name: "Marseille" },
    };
    const point = known[String(postal_code).slice(0, 2)] || { lat: 46.6, lng: 2.4, name: city };
    return { ...point, display_name: `${street}, ${postal_code} ${city}, France (aperçu)` };
  }
  const q = `${street}, ${postal_code} ${city}, France`;
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=fr&addressdetails=1&q=${encodeURIComponent(q)}`;
  const res = await fetch(url, {
    headers: { "Accept-Language": "fr" },
  });
  if (!res.ok) throw new Error("Service de localisation indisponible");
  const data = await res.json();
  if (!data || !data.length) {
    throw new Error("Adresse introuvable — vérifiez le numéro, le code postal et la ville");
  }
  return {
    lat: parseFloat(data[0].lat),
    lng: parseFloat(data[0].lon),
    display_name: data[0].display_name,
  };
}

export default function CreateMission() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "", description: "",
    // Adresse départ
    pickup_street: "", pickup_postal_code: "", pickup_city: "",
    // Adresse livraison
    delivery_street: "", delivery_postal_code: "", delivery_city: "",
    pickup_date: null, pickup_time: "",
    cargo_weight_kg: "", cargo_volume_m3: "",
    vehicle_type: "van",
    price: "",
    declared_value_eur: "",
    estimated_tolls_eur: "",
    exclusions_confirmed: false,
    contact_phone: user?.phone || "",
  });
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pending, setPending] = useState(null); // { payload, display }
  const [errors, setErrors] = useState({});
  const [duplicating, setDuplicating] = useState(false);
  // Etat de géocodage par adresse: { ok: bool, message, lat, lng } | null
  const [geoPickup, setGeoPickup] = useState(null);
  const [geoDelivery, setGeoDelivery] = useState(null);
  const [geoLoading, setGeoLoading] = useState({ pickup: false, delivery: false });
  const [vehicleManuallyPicked, setVehicleManuallyPicked] = useState(false);
  // Données calculées par Mapbox Directions: { distance_km, duration_min, avg_speed_kmh, zone }
  const [routeInfo, setRouteInfo] = useState(null);

  // L'utilisateur peut arriver après le premier rendu du formulaire. Dans ce
  // cas, préremplir le téléphone sans écraser une saisie déjà commencée.
  useEffect(() => {
    if (!user?.phone) return;
    setForm((prev) => prev.contact_phone ? prev : { ...prev, contact_phone: user.phone });
  }, [user?.phone]);

  // Duplication d'une mission existante via ?duplicate=<mission_id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dupId = params.get("duplicate");
    if (!dupId) return;
    setDuplicating(true);
    api.get(`/missions/${dupId}`).then(({ data }) => {
      // Si l'adresse est au format "rue, CP ville" on essaie de la splitter
      const splitAddr = (addr) => {
        const parts = (addr || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (parts.length < 2) return { street: addr || "", postal_code: "", city: "" };
        const last = parts[parts.length - 1];
        const m = last.match(/^(\d{4,5})\s+(.+)$/);
        if (m) {
          return {
            street: parts.slice(0, -1).join(", "),
            postal_code: m[1],
            city: m[2],
          };
        }
        return { street: parts.slice(0, -1).join(", "), postal_code: "", city: last };
      };
      const pu = splitAddr(data.pickup_address);
      const dl = splitAddr(data.delivery_address);
      setForm((f) => ({
        ...f,
        title: data.title || "",
        description: data.description || "",
        pickup_street: pu.street,
        pickup_postal_code: pu.postal_code,
        pickup_city: pu.city,
        delivery_street: dl.street,
        delivery_postal_code: dl.postal_code,
        delivery_city: dl.city,
        cargo_weight_kg: data.cargo_weight_kg || "",
        cargo_volume_m3: data.cargo_volume_m3 || "",
        vehicle_type: data.vehicle_type || "van",
        contact_phone: data.contact_phone || user?.phone || "",
        // Date NON pré-remplie (l'utilisateur doit la choisir)
        pickup_date: null, pickup_time: "",
      }));
      setVehicleManuallyPicked(true);
      toast.info("Mission dupliquée — choisissez une nouvelle date d'enlèvement");
    }).catch(() => {
      toast.error("Mission à dupliquer introuvable");
    }).finally(() => setDuplicating(false));
  }, [user?.phone]);

  const up = (k) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [k]: value }));
    if (/^pickup_(street|postal_code|city)$/.test(k)) setGeoPickup(null);
    if (/^delivery_(street|postal_code|city)$/.test(k)) setGeoDelivery(null);
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  // Récupère le trajet routier réel via Mapbox Directions dès que les 2 adresses sont géocodées
  // Détermine la zone (city/countryside/mixed) à partir de la vitesse moyenne du trajet
  useEffect(() => {
    if (!geoPickup?.ok || !geoDelivery?.ok) { setRouteInfo(null); return; }
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) {
      // Fallback: distance haversine, zone "mixed"
      const dist = haversineKm(geoPickup.lat, geoPickup.lng, geoDelivery.lat, geoDelivery.lng);
      setRouteInfo({ distance_km: dist, duration_min: null, avg_speed_kmh: null, zone: "mixed" });
      return;
    }
    let aborted = false;
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${geoPickup.lng},${geoPickup.lat};${geoDelivery.lng},${geoDelivery.lat}?overview=false&access_token=${token}`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        if (aborted) return;
        const route = d?.routes?.[0];
        if (!route) {
          const dist = haversineKm(geoPickup.lat, geoPickup.lng, geoDelivery.lat, geoDelivery.lng);
          setRouteInfo({ distance_km: dist, zone: "mixed", avg_speed_kmh: null });
          return;
        }
        const distKm = route.distance / 1000;
        const durMin = route.duration / 60;
        const avg = distKm / (route.duration / 3600); // km/h
        // Détermination de la zone selon la vitesse moyenne
        // < 35 km/h → urbain dense, 35-65 → mixte, > 65 → campagne/route
        let zone = "mixed";
        if (avg < 35) zone = "city";
        else if (avg > 65) zone = "countryside";
        setRouteInfo({ distance_km: distKm, duration_min: durMin, avg_speed_kmh: avg, zone });
      })
      .catch(() => {
        const dist = haversineKm(geoPickup.lat, geoPickup.lng, geoDelivery.lat, geoDelivery.lng);
        setRouteInfo({ distance_km: dist, zone: "mixed", avg_speed_kmh: null });
      });
    return () => { aborted = true; };
  }, [geoPickup, geoDelivery]);

  // Suggère automatiquement le bon véhicule à partir du poids et du volume — sauf si l'utilisateur a choisi manuellement
  useEffect(() => {
    if (vehicleManuallyPicked) return;
    const w = parseFloat(form.cargo_weight_kg);
    if (!w || w <= 0) return;
    const suggested = suggestVehicleByWeightVolume(w, parseFloat(form.cargo_volume_m3) || 0);
    if (suggested && suggested.id !== form.vehicle_type) {
      setForm((f) => ({ ...f, vehicle_type: suggested.id }));
    }
  }, [form.cargo_weight_kg, form.cargo_volume_m3, vehicleManuallyPicked]); // eslint-disable-line react-hooks/exhaustive-deps

  const weightNum = parseFloat(form.cargo_weight_kg) || 0;
  const volumeNum = parseFloat(form.cargo_volume_m3) || 0;
  const vehicleCompatible = isVehicleCompatible(form.vehicle_type, weightNum, volumeNum);

  // Distance: priorité au calcul Mapbox (réel), sinon haversine immédiat
  const distanceKm = useMemo(() => {
    if (routeInfo?.distance_km) return routeInfo.distance_km;
    if (geoPickup?.ok && geoDelivery?.ok) {
      return haversineKm(geoPickup.lat, geoPickup.lng, geoDelivery.lat, geoDelivery.lng);
    }
    return null;
  }, [routeInfo, geoPickup, geoDelivery]);
  const detectedZone = routeInfo?.zone || "mixed";

  // Utilise la même date/heure locale que celle affichée à l'expéditeur pour
  // appliquer les majorations nuit, week-end et jour férié.
  const pickupWhen = useMemo(() => {
    if (!form.pickup_date) return null;
    const date = new Date(form.pickup_date);
    if (Number.isNaN(date.getTime())) return null;
    if (form.pickup_time && /^\d{2}:\d{2}$/.test(form.pickup_time)) {
      const [hours, minutes] = form.pickup_time.split(":").map(Number);
      date.setHours(hours, minutes, 0, 0);
    }
    return date;
  }, [form.pickup_date, form.pickup_time]);

  // Les péages sont saisis comme une estimation réelle, pas comme un forfait/km.
  const pricing = useMemo(() => {
    return computeSuggestedPrice({
      weightKg: parseFloat(form.cargo_weight_kg) || 0,
      volumeM3: volumeNum,
      distanceKm: distanceKm || 0,
      vehicleType: form.vehicle_type,
      zone: detectedZone,
      includeToll: false,
      tollsEur: parseFloat(form.estimated_tolls_eur) || 0,
      when: pickupWhen,
    });
  }, [form.cargo_weight_kg, volumeNum, distanceKm, form.vehicle_type, detectedZone, pickupWhen, form.estimated_tolls_eur]);

  const verifyAddress = async (kind) => {
    const street = form[`${kind}_street`];
    const cp = form[`${kind}_postal_code`];
    const city = form[`${kind}_city`];
    if (!street || !cp || !city) return;
    if (!/^\d{5}$/.test(cp.trim())) {
      const set = kind === "pickup" ? setGeoPickup : setGeoDelivery;
      set({ ok: false, message: "Code postal invalide (5 chiffres)" });
      return;
    }
    setGeoLoading((l) => ({ ...l, [kind]: true }));
    try {
      const g = await geocode({ street, postal_code: cp, city });
      const set = kind === "pickup" ? setGeoPickup : setGeoDelivery;
      set({ ok: true, message: g.display_name, lat: g.lat, lng: g.lng });
    } catch (e) {
      const set = kind === "pickup" ? setGeoPickup : setGeoDelivery;
      set({ ok: false, message: e.message });
    } finally {
      setGeoLoading((l) => ({ ...l, [kind]: false }));
    }
  };

  const validateFields = () => {
    const e = {};
    if (!form.title || form.title.trim().length < 3) e.title = "Titre requis (min 3 caractères)";
    if (!form.description || form.description.trim().length < 5) e.description = "Description requise (min 5 caractères)";
    const w = parseFloat(form.cargo_weight_kg);
    if (!form.cargo_weight_kg || isNaN(w) || w <= 0) e.cargo_weight_kg = "Poids requis (> 0 kg)";
    const v = parseFloat(form.cargo_volume_m3);
    if (form.cargo_volume_m3 === "" || isNaN(v) || v < 0) e.cargo_volume_m3 = "Volume requis (≥ 0 m³)";
    if (form.declared_value_eur !== "") {
      const declared = parseFloat(form.declared_value_eur);
      if (isNaN(declared) || declared < 0 || declared > 10000) e.declared_value_eur = "Valeur entre 0 et 10 000 €";
    }
    if (form.estimated_tolls_eur !== "") {
      const tolls = parseFloat(form.estimated_tolls_eur);
      if (isNaN(tolls) || tolls < 0 || tolls > 10000) e.estimated_tolls_eur = "Péages entre 0 et 10 000 €";
    }
    if (!form.exclusions_confirmed) e.exclusions_confirmed = "Confirmez le respect des exclusions MML";
    if (!form.contact_phone || !/^[+0-9][0-9 ().-]{5,19}$/.test(form.contact_phone.trim())) e.contact_phone = "Téléphone de contact invalide";
    if (!form.pickup_date) e.pickup_date = "Date d'enlèvement requise";
    return e;
  };

  // Étape 1 : valide + géocode + calcule le prix, puis ouvre le récapitulatif (ne crée PAS encore)
  const prepareAndReview = async (e) => {
    e.preventDefault();
    if (reviewing || loading) return;
    const fieldErrs = validateFields();
    if (Object.keys(fieldErrs).length > 0) {
      setErrors(fieldErrs);
      toast.error("Veuillez corriger les champs en rouge");
      return;
    }
    setErrors({});
    setReviewing(true);
    try {
      let pickup = geoPickup;
      let delivery = geoDelivery;
      if (!pickup?.ok) {
        if (form.pickup_street && /^\d{5}$/.test(form.pickup_postal_code) && form.pickup_city) {
          try {
            const g = await geocode({ street: form.pickup_street, postal_code: form.pickup_postal_code, city: form.pickup_city });
            pickup = { ok: true, message: g.display_name, lat: g.lat, lng: g.lng };
            setGeoPickup(pickup);
          } catch (er) { setGeoPickup({ ok: false, message: er.message }); }
        }
      }
      if (!delivery?.ok) {
        if (form.delivery_street && /^\d{5}$/.test(form.delivery_postal_code) && form.delivery_city) {
          try {
            const g = await geocode({ street: form.delivery_street, postal_code: form.delivery_postal_code, city: form.delivery_city });
            delivery = { ok: true, message: g.display_name, lat: g.lat, lng: g.lng };
            setGeoDelivery(delivery);
          } catch (er) { setGeoDelivery({ ok: false, message: er.message }); }
        }
      }
      if (!pickup?.ok || !delivery?.ok) {
        toast.error("Vérifiez les deux adresses avant de continuer");
        return;
      }
      const distKm = distanceKm || haversineKm(pickup.lat, pickup.lng, delivery.lat, delivery.lng);
      const finalPricing = computeSuggestedPrice({
        weightKg: parseFloat(form.cargo_weight_kg) || 0,
        volumeM3: parseFloat(form.cargo_volume_m3) || 0,
        distanceKm: distKm,
        vehicleType: form.vehicle_type,
        zone: routeInfo?.zone || "mixed",
        includeToll: false,
        tollsEur: parseFloat(form.estimated_tolls_eur) || 0,
        when: pickupWhen,
      });
      if (!finalPricing || finalPricing.total <= 0) {
        toast.error("Renseignez le poids pour calculer le tarif");
        return;
      }
      if (!isVehicleCompatible(form.vehicle_type, parseFloat(form.cargo_weight_kg) || 0, parseFloat(form.cargo_volume_m3) || 0)) {
        toast.error("Le véhicule sélectionné est trop petit pour ce poids et ce volume");
        return;
      }
      const enteredPrice = form.price === "" ? finalPricing.total : parseFloat(form.price);
      if (!Number.isFinite(enteredPrice) || enteredPrice <= 0) {
        toast.error("Saisissez un prix supérieur à 0 € ou utilisez l'estimation proposée");
        return;
      }
      const dateStr = format(form.pickup_date, "yyyy-MM-dd");
      const timePart = form.pickup_time && /^\d{2}:\d{2}$/.test(form.pickup_time) ? form.pickup_time : null;
      const combinedDate = timePart ? `${dateStr}T${timePart}` : dateStr;
      const pickup_address = `${form.pickup_street}, ${form.pickup_postal_code} ${form.pickup_city}`;
      const delivery_address = `${form.delivery_street}, ${form.delivery_postal_code} ${form.delivery_city}`;
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        pickup_address,
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        delivery_address,
        delivery_lat: delivery.lat,
        delivery_lng: delivery.lng,
        pickup_date: combinedDate,
        cargo_weight_kg: parseFloat(form.cargo_weight_kg),
        cargo_volume_m3: parseFloat(form.cargo_volume_m3),
        vehicle_type: form.vehicle_type,
        transport_mode: "dedicated",
        exclusions_confirmed: true,
        declared_value_eur: form.declared_value_eur === "" ? null : parseFloat(form.declared_value_eur),
        estimated_tolls_eur: parseFloat(form.estimated_tolls_eur) || 0,
        price: Math.round(enteredPrice * 100) / 100,
        contact_phone: form.contact_phone.trim(),
        distance_km: Math.round(distKm),
        pricing_zone: routeInfo?.zone || "mixed",
      };
      const vehicleLabel = (VEHICLE_TYPES.find((vt) => vt.id === form.vehicle_type) || {}).label || form.vehicle_type;
      const display = {
        pickup_address, delivery_address,
        dateLabel: format(form.pickup_date, "dd MMMM yyyy", { locale: fr }) + (timePart ? ` à ${timePart}` : ""),
        vehicleLabel,
        distanceKm: Math.round(distKm),
        price: Math.round(enteredPrice * 100) / 100,
      };
      setPending({ payload, display });
      setReviewOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReviewing(false);
    }
  };

  // Étape 2 : création réelle après confirmation dans le récapitulatif
  const confirmCreate = async () => {
    if (loading || !pending) return; // empêche le double-clic
    setLoading(true);
    try {
      const { data } = await api.post("/missions", pending.payload);
      toast.success("Mission publiée");
      setReviewOpen(false);
      nav(`/missions/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="create-mission">
      <BackButton to="/shipper" label="Retour au tableau de bord" />
      <div className="overline text-accent mb-2">— Nouvelle mission</div>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-8">Publier une mission</h1>

      <div className="mb-6 rounded-md border border-[#1F4032]/15 bg-[#1F4032]/5 p-4 text-sm text-[#1F4032]/90" data-testid="mission-rules-notice">
        <div className="font-display font-bold mb-1">Ce que MML transporte</div>
        <p className="text-xs leading-relaxed">
          France métropolitaine (hors Corse) · max <b>6 000 kg</b> et <b>40 m³</b> par envoi · valeur ≤ <b>10 000 €</b>.
          Sont exclus : animaux vivants, matières dangereuses, marchandises sous température dirigée,
          déchets réglementés et médicaments à contraintes. Les déménagements complets sont acceptés
          dans les limites de la plateforme. Les biens doivent être
          emballés et prêts à charger.
        </p>
      </div>
      <form onSubmit={prepareAndReview} noValidate className="space-y-6">
        <div>
          <Label className="overline">Titre</Label>
          <Input required minLength={3} value={form.title} onChange={up("title")}
            placeholder="Ex: Transport 3 palettes Paris-Lyon"
            aria-invalid={!!errors.title}
            className={`rounded-md mt-1 h-11 ${errors.title ? "border-red-500" : ""}`} data-testid="mission-title" />
          {errors.title && <p className="text-xs text-red-600 mt-1" data-testid="mission-title-error">{errors.title}</p>}
        </div>
        <div>
          <Label className="overline">Description marchandise</Label>
          <Textarea required minLength={5} value={form.description} onChange={up("description")}
            aria-invalid={!!errors.description}
            className={`rounded-md mt-1 min-h-24 ${errors.description ? "border-red-500" : ""}`} data-testid="mission-description" />
          {errors.description && <p className="text-xs text-red-600 mt-1" data-testid="mission-description-error">{errors.description}</p>}
        </div>

        <AddressBlock
          kind="pickup" title="Adresse de départ"
          form={form} up={up}
          geo={geoPickup} loading={geoLoading.pickup}
          onVerify={() => verifyAddress("pickup")}
        />
        <AddressBlock
          kind="delivery" title="Adresse de livraison"
          form={form} up={up}
          geo={geoDelivery} loading={geoLoading.delivery}
          onVerify={() => verifyAddress("delivery")}
        />

        <div className="grid md:grid-cols-4 gap-4 border-t border-neutral-200 pt-6">
          <div>
            <Label className="overline">Date enlèvement</Label>
            <Input
              type="date" data-testid="pickup-date" aria-label="Date d'enlèvement"
              className="w-full mt-1 h-11"
              min={format(new Date(), "yyyy-MM-dd")}
              value={form.pickup_date ? format(form.pickup_date, "yyyy-MM-dd") : ""}
              onInput={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, pickup_date: value ? new Date(`${value}T12:00:00`) : null }));
                if (errors.pickup_date) setErrors((prev) => ({ ...prev, pickup_date: undefined }));
              }}
            />
            {errors.pickup_date && <p className="text-xs text-red-600 mt-1" data-testid="pickup-date-error">{errors.pickup_date}</p>}
          </div>
          <div>
            <Label className="overline">Heure d'enlèvement</Label>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder="HH:MM"
              value={form.pickup_time}
              onChange={(e) => {
                let v = e.target.value.replace(/[^\d:]/g, "");
                if (v.length === 2 && form.pickup_time.length < 2 && !v.includes(":")) v = v + ":";
                setForm((prev) => ({ ...prev, pickup_time: v.slice(0, 5) }));
              }}
              className="rounded-md mt-1 h-11"
              data-testid="pickup-time"
            />
          </div>
          <div>
            <Label className="overline">Poids (kg)</Label>
            <Input required type="number" step="0.1" min="0.1" value={form.cargo_weight_kg}
              onChange={up("cargo_weight_kg")}
              aria-invalid={!!errors.cargo_weight_kg}
              className={`rounded-md mt-1 h-11 ${errors.cargo_weight_kg ? "border-red-500" : ""}`} data-testid="cargo-weight" />
            {errors.cargo_weight_kg && <p className="text-xs text-red-600 mt-1" data-testid="cargo-weight-error">{errors.cargo_weight_kg}</p>}
          </div>
          <div>
            <Label className="overline">Volume (m³)</Label>
            <Input required type="number" step="0.1" min="0" value={form.cargo_volume_m3}
              onChange={up("cargo_volume_m3")}
              aria-invalid={!!errors.cargo_volume_m3}
              className={`rounded-md mt-1 h-11 ${errors.cargo_volume_m3 ? "border-red-500" : ""}`} data-testid="cargo-volume" />
            {errors.cargo_volume_m3 && <p className="text-xs text-red-600 mt-1" data-testid="cargo-volume-error">{errors.cargo_volume_m3}</p>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
          <Label className="overline">Valeur déclarée (€, facultatif)</Label>
          <Input type="number" min="0" max="10000" step="0.01" value={form.declared_value_eur}
            onChange={up("declared_value_eur")} placeholder="Jusqu'à 10 000 €"
            aria-invalid={!!errors.declared_value_eur}
            className={`rounded-md mt-1 h-11 ${errors.declared_value_eur ? "border-red-500" : ""}`} data-testid="declared-value" />
          {errors.declared_value_eur && <p className="text-xs text-red-600 mt-1">{errors.declared_value_eur}</p>}
          </div>
          <div>
            <Label className="overline">Péages estimés (€, facultatif)</Label>
            <Input type="number" min="0" max="10000" step="0.01" value={form.estimated_tolls_eur}
              onChange={up("estimated_tolls_eur")} placeholder="Montant réel estimé"
              aria-invalid={!!errors.estimated_tolls_eur}
              className={`rounded-md mt-1 h-11 ${errors.estimated_tolls_eur ? "border-red-500" : ""}`} data-testid="estimated-tolls" />
            {errors.estimated_tolls_eur && <p className="text-xs text-red-600 mt-1">{errors.estimated_tolls_eur}</p>}
          </div>
        </div>

        {(() => {
          const cls = classifyMMLClass(form.cargo_weight_kg, form.cargo_volume_m3);
          if (!form.cargo_weight_kg && !form.cargo_volume_m3) return null;
          return cls ? (
            <div className="text-xs text-[#1F4032] bg-[#1F4032]/5 border border-[#1F4032]/15 rounded-md px-3 py-2" data-testid="mmt-class-indicator">
              Classe MML : <b>{cls.label}</b> (déterminée par poids + volume). Le tarif indicatif suit la grille Annexe C ; vous restez libre de votre prix.
            </div>
          ) : (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="mmt-class-toobig">
              Au-delà de la plus grande classe (6 000 kg / 40 m³) : envoi non éligible.
            </div>
          );
        })()}

        <div>
          <Label className="overline">Véhicule requis</Label>
          <select required value={form.vehicle_type}
            onChange={(e) => {
              const value = e.target.value;
              setVehicleManuallyPicked(true);
              setForm((prev) => ({ ...prev, vehicle_type: value }));
            }}
            className="w-full rounded-md mt-1 h-11 border border-input px-3 bg-background"
            data-testid="mission-vehicle">
            {VEHICLE_TYPES.map((v) => {
              const incompatible = (weightNum > 0 && v.max_weight_kg < weightNum) ||
                (volumeNum > 0 && v.max_volume_m3 < volumeNum);
              return (
                <option key={v.id} value={v.id} disabled={incompatible}>
                  {v.label} (jusqu'à {v.max_weight_kg >= 1000 ? `${v.max_weight_kg/1000} t` : `${v.max_weight_kg} kg`})
                  {incompatible ? " — capacité insuffisante" : ""}
                </option>
              );
            })}
          </select>
          {weightNum > 0 && !vehicleCompatible && (
            <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Véhicule trop petit pour {weightNum} kg et {volumeNum} m³
            </div>
          )}
          {weightNum > 0 && vehicleCompatible && !vehicleManuallyPicked && (
            <div className="text-xs text-muted-foreground mt-1">
              Véhicule suggéré automatiquement selon le poids
            </div>
          )}
        </div>

        {/* Tarif calculé automatiquement */}
        <div className="border border-[#1F4032]/15 bg-[#1F4032]/5 rounded-md p-5">
          <div className="flex items-start gap-3">
            <Calculator className="w-5 h-5 text-[#1F4032] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="overline text-[#1F4032]">Estimation indicative de la grille MML</div>
              {pricing ? (
                <>
                  <div className="flex items-baseline gap-3 mt-1">
                    <div className="font-display font-black text-3xl tracking-tighter text-[#1F4032]" data-testid="computed-price">
                      {pricing.total} €
                    </div>
                    <div className="text-xs text-muted-foreground">
                      (hypothèse TVA 20 % et commission MML 15 %)
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    {pricing.vehicleLabel} · Base <b>{pricing.baseRate.toFixed(2)} €/km</b>
                    {pricing.tolls > 0 && <> · Péages estimés <b>{pricing.tolls.toFixed(2)} €</b></>}
                    <br />
                    Trajet détecté : <b>{ZONE_LABEL[detectedZone]}</b>
                    {routeInfo?.avg_speed_kmh && (
                      <> (vitesse moyenne {Math.round(routeInfo.avg_speed_kmh)} km/h)</>
                    )}
                    {routeInfo?.duration_min && (
                      <> · Durée estimée {Math.round(routeInfo.duration_min)} min</>
                    )}
                    <br />Cette estimation est facultative : vous pouvez conserver, augmenter ou diminuer le prix proposé.
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground mt-1">
                  Renseignez les <b>2 adresses</b> et le <b>poids</b> pour afficher le tarif automatiquement.
                </div>
                )}
              <div className="mt-4">
                <Label className="overline">Prix proposé par l'expéditeur</Label>
                <Input
                  type="number" min="0.01" step="0.01"
                  value={form.price}
                  onChange={up("price")}
                  placeholder={pricing ? String(pricing.total) : "Saisir un prix"}
                  className="rounded-md mt-1 h-11"
                  data-testid="mission-price"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Le montant sera verrouillé après le choix du transporteur. Le transporteur verra le montant avant et après la commission MML.
                </p>
              </div>
              <label className="mt-4 flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.exclusions_confirmed}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({ ...prev, exclusions_confirmed: checked }));
                    if (errors.exclusions_confirmed) setErrors((prev) => ({ ...prev, exclusions_confirmed: undefined }));
                  }}
                  className="mt-0.5 h-4 w-4 accent-[#1F4032]"
                  data-testid="exclusions-confirmed"
                />
                <span>Je confirme que la marchandise respecte les exclusions, les plafonds de poids/volume/valeur et que le transport est dédié.</span>
              </label>
              {errors.exclusions_confirmed && <p className="text-xs text-red-600 mt-1">{errors.exclusions_confirmed}</p>}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-1 gap-6">
          <div>
            <Label className="overline">Téléphone contact</Label>
            <Input required type="tel" value={form.contact_phone} onChange={up("contact_phone")}
              placeholder={user?.phone ? "" : "06 12 34 56 78"}
              aria-invalid={!!errors.contact_phone}
              className={`rounded-md mt-1 h-11 ${errors.contact_phone ? "border-red-500" : ""}`} data-testid="mission-phone" />
            {errors.contact_phone && <p className="text-xs text-red-600 mt-1" data-testid="mission-phone-error">{errors.contact_phone}</p>}
            {user?.phone && form.contact_phone === user.phone && (
              <p className="text-[11px] text-neutral-500 mt-1">
                Pré-rempli depuis votre profil — modifiable
              </p>
            )}
          </div>
        </div>

        <div className="border-t border-neutral-200 pt-6 flex gap-3">
          <Button type="submit" disabled={reviewing || loading}
            className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] h-11 px-6 gap-2"
            data-testid="mission-submit">
            {reviewing ? <><Loader2 className="w-4 h-4 animate-spin" /> Vérification…</> : "Vérifier la mission"}
          </Button>
          <Button type="button" variant="outline" onClick={() => nav(-1)}
            className="rounded-full border-neutral-300 h-11">Annuler</Button>
        </div>
      </form>

      {/* Récapitulatif avant validation définitive */}
      <Dialog open={reviewOpen} onOpenChange={(o) => { if (!loading) setReviewOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="mission-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
              <Package className="w-5 h-5 text-[#1F4032]" /> Vérifiez votre mission
            </DialogTitle>
            <DialogDescription>
              Vérifiez les informations ci-dessous avant la publication définitive.
            </DialogDescription>
          </DialogHeader>
          {pending && (
            <div className="space-y-3 text-sm" data-testid="mission-review-body">
              <div className="border-b border-neutral-100 pb-2">
                <div className="overline text-neutral-500">Mission</div>
                <div className="font-display font-bold text-base">{form.title}</div>
                <div className="text-neutral-600 text-xs mt-0.5">{form.description}</div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <ReviewRow label="Départ" value={pending.display.pickup_address} />
                <ReviewRow label="Livraison" value={pending.display.delivery_address} />
                <ReviewRow label="Date d'enlèvement" value={pending.display.dateLabel} />
                <ReviewRow label="Marchandise" value={`${form.cargo_weight_kg} kg · ${form.cargo_volume_m3} m³`} />
                <ReviewRow label="Véhicule" value={pending.display.vehicleLabel} />
                <ReviewRow label="Distance" value={`${pending.display.distanceKm} km`} />
                <ReviewRow label="Contact" value={form.contact_phone} />
              </div>
              <div className="flex items-baseline justify-between border-t border-neutral-200 pt-3">
                <span className="overline text-[#1F4032]">Prix choisi</span>
                <span className="font-display font-black text-2xl text-[#1F4032]" data-testid="review-price">{pending.display.price} €</span>
              </div>
              <div className="text-xs text-neutral-500 text-right">
                Commission MML standard estimée : {(pending.display.price * 0.15).toFixed(2)} € · transporteur : {(pending.display.price * 0.85).toFixed(2)} €
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" disabled={loading}
              onClick={() => setReviewOpen(false)}
              className="rounded-full border-neutral-300 h-11" data-testid="mission-review-edit">
              Modifier
            </Button>
            <Button type="button" onClick={confirmCreate} disabled={loading}
              className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white h-11 px-6 gap-2"
              data-testid="mission-review-confirm">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><CheckCircle2 className="w-4 h-4" /> Confirmer et publier</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span className="font-medium text-right text-[#15302A]">{value}</span>
    </div>
  );
}

function AddressBlock({ kind, title, form, up, geo, loading, onVerify }) {
  return (
    <div className="border-t border-neutral-200 pt-6 space-y-3" data-testid={`${kind}-address-block`}>
      <div className="flex items-center justify-between">
        <Label className="overline flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> {title}
        </Label>
        {geo?.ok && (
          <span className="text-[11px] font-mono text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Localisée
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-12 gap-3">
        <Input required value={form[`${kind}_street`]} onChange={up(`${kind}_street`)}
          onBlur={onVerify}
          placeholder="N° et nom de rue (ex: 12 rue de Rivoli)"
          className="md:col-span-7 rounded-md h-11"
          data-testid={`${kind}-street`} />
        <Input required value={form[`${kind}_postal_code`]} onChange={up(`${kind}_postal_code`)}
          onBlur={onVerify}
          placeholder="Code postal"
          inputMode="numeric"
          maxLength={5}
          className="md:col-span-2 rounded-md h-11 font-mono"
          data-testid={`${kind}-postal`} />
        <Input required value={form[`${kind}_city`]} onChange={up(`${kind}_city`)}
          onBlur={onVerify}
          placeholder="Ville"
          className="md:col-span-3 rounded-md h-11"
          data-testid={`${kind}-city`} />
      </div>
      {loading && (
        <p className="text-xs text-neutral-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Vérification de l'adresse…
        </p>
      )}
      {!loading && geo?.ok && (
        <p className="text-xs text-emerald-700 flex items-start gap-2">
          <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{geo.message}</span>
        </p>
      )}
      {!loading && geo?.ok === false && (
        <p className="text-xs text-red-600 flex items-start gap-2">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{geo.message}</span>
        </p>
      )}
    </div>
  );
}
